import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Loader2, Eye, EyeOff, KeyRound, Check, X, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  // Password strength validation
  const passwordChecks = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    match: newPassword === confirmPassword && newPassword.length > 0,
  };

  const isValidPassword = Object.values(passwordChecks).every(Boolean);

  useEffect(() => {
    if (!token) {
      toast.error('Token tidak sah');
      navigate('/login');
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidPassword) {
      toast.error('Sila pastikan password memenuhi semua syarat');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: newPassword,
      });
      
      toast.success('Password berjaya ditukar! Sila log masuk.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal reset password. Token mungkin telah tamat tempoh.');
    } finally {
      setLoading(false);
    }
  };

  const PasswordCheck = ({ valid, text }) => (
    <div className={`flex items-center gap-2 text-xs transition-all duration-300 ${valid ? 'text-emerald-400' : 'text-gray-500'}`}>
      {valid ? (
        <Check className="w-3 h-3" />
      ) : (
        <X className="w-3 h-3" />
      )}
      <span>{text}</span>
    </div>
  );

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#030308]">
      {/* Animated gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a12] via-[#030308] to-[#050510]" />
        
        {/* Animated glow orbs */}
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/10 blur-[120px] animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute bottom-[-30%] right-[-10%] w-[700px] h-[700px] rounded-full bg-gradient-to-r from-cyan-500/15 to-blue-500/10 blur-[150px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }} />
        
        {/* Grid overlay */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8 space-y-4">
            <div className="relative inline-block">
              <div className="absolute inset-0 blur-xl bg-gradient-to-r from-amber-400/30 to-orange-500/30 rounded-full" />
              <div className="relative bg-gradient-to-br from-amber-500/20 to-orange-500/20 p-4 rounded-2xl border border-amber-500/20">
                <KeyRound className="w-12 h-12 text-amber-400" />
              </div>
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent">
                Reset Password
              </h1>
              <p className="text-sm text-gray-400">
                Masukkan password baru untuk akaun anda
              </p>
            </div>
          </div>

          {/* Form Card */}
          <div className="relative group">
            {/* Card glow effect */}
            <div className="absolute -inset-[1px] bg-gradient-to-r from-amber-500/50 via-orange-500/50 to-red-500/50 rounded-2xl blur-sm opacity-75 group-hover:opacity-100 transition-opacity duration-500" />
            
            <div className="relative bg-[#0a0a12]/90 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* New Password */}
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-xs uppercase tracking-wider text-gray-400 flex items-center gap-2">
                    <Lock size={12} className="text-amber-400" /> Password Baru
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="bg-white/5 border-white/10 focus:border-amber-500/50 focus:ring-amber-500/20 text-white rounded-xl pl-4 pr-12 py-3 transition-all duration-300"
                      placeholder="••••••••"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-amber-400 transition-colors"
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-xs uppercase tracking-wider text-gray-400 flex items-center gap-2">
                    <Lock size={12} className="text-orange-400" /> Sahkan Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="bg-white/5 border-white/10 focus:border-orange-500/50 focus:ring-orange-500/20 text-white rounded-xl pl-4 pr-12 py-3 transition-all duration-300"
                      placeholder="••••••••"
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-400 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Password Requirements */}
                <div className="bg-white/5 rounded-xl p-4 space-y-2">
                  <p className="text-xs uppercase tracking-wider text-gray-400 mb-3">Syarat Password:</p>
                  <PasswordCheck valid={passwordChecks.length} text="Minimum 8 aksara" />
                  <PasswordCheck valid={passwordChecks.uppercase} text="Sekurang-kurangnya 1 huruf besar" />
                  <PasswordCheck valid={passwordChecks.lowercase} text="Sekurang-kurangnya 1 huruf kecil" />
                  <PasswordCheck valid={passwordChecks.number} text="Sekurang-kurangnya 1 nombor" />
                  <PasswordCheck valid={passwordChecks.match} text="Password sama" />
                </div>

                <Button
                  type="submit"
                  className="w-full relative overflow-hidden group/btn bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white font-medium py-3 rounded-xl transition-all duration-300 shadow-lg shadow-amber-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !isValidPassword}
                  data-testid="submit-button"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Menyimpan...</>
                    ) : (
                      <>
                        Reset Password
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-700" />
                </Button>

                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full text-sm text-gray-400 hover:text-amber-400 transition-colors"
                  data-testid="back-to-login"
                >
                  ← Kembali ke Log Masuk
                </button>
              </form>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-600">
              © 2025 Underads. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
